import cx from 'classnames'
import ScrollView from 'components/ScrollView'
import Spinner from 'components/Spinner'
import { useObservableCallback } from 'hooks/rx-hooks'
import { getQuestionComponentRegistryEntry } from 'jobs/questions/component-registry'
import { observer } from 'mobx-react'
import * as React from 'react'
import { useSyncStepWithHash } from '../../../hooks/sync-step-with-hash'
import { QuestionStepViewModel } from '../../../IntakeQuestionsViewModel'
import JobCreationViewModel from '../../../JobCreationViewModel'
import LegendSelectorViewModel from '../../../LegendSelectorViewModel'
import { StepContainer } from '../../../OutlineViewModel'
import PreviousProviderSelectionViewModel from '../../../PreviousProviderSelectionViewModel'
import SubmissionViewModel from '../../../SubmissionViewModel'
import TeamSelectorViewModel from '../../../TeamSelectorViewModel'
import WorkspaceSelectorViewModel from '../../../WorkspaceSelectorViewModel'
import PreviousProviderSelector from '../PreviousProviderSelector'
import CompactLegendSelector from './CompactLegendSelector'
import CompactTeamSelector from './CompactTeamSelector'
import CompactWorkspaceSelector from './CompactWorkspaceSelector'
import FormSubmission from './FormSubmission'
import Step from './Step'
import styles from './JobCreationFormView.sass'
import NameInputViewModel from 'jobs/creation/NameViewModel'
import NameInput from './NameInput'
import OwnerSelectorViewModel from 'jobs/creation/OwnerSelectorViewModel'
import CompactOwnerSelector from './CompactOwnerSelector'
import RequestProviderSelectorViewModel from 'jobs/creation/RequestProviderSelectorViewModel'
import CompactRequestProviderSelector from './CompactRequestProviderSelector'

interface JobCreationFormViewProps {
  vm: JobCreationViewModel
}

/**
 * The JobCreationFormView
 */
export default observer(function JobCreationFormView({
  vm,
}: JobCreationFormViewProps) {
  useSyncStepWithHash(vm.outline)

  return (
    <ScrollView>
      <div className={cx(styles.root)}>
        <div className={styles.container}>
          {vm.outline.stepsContainers.map((stepContainer) => (
            <StepView
              key={stepContainer.step.stepId}
              stepContainer={stepContainer}
            />
          ))}
          {/* Only display the loader if Submission is not being displayed. */}
          {!vm.outline.stepsContainers.includes(vm.submission) &&
            (vm.intakeQuestions.querying ||
              vm.legendSelector.isFetchingLegendDetails ||
              vm.persister.persisting) && <LoadingFormContent />}
        </div>
      </div>
    </ScrollView>
  )
})

interface StepViewProps {
  stepContainer: StepContainer
}

/**
 * Container for a Step, renders the proper content based on the container type.
 */
const StepView = observer(function StepView({ stepContainer }: StepViewProps) {
  const step = stepContainer.step

  useObservableCallback(step.becomeActive$, step.onBecameActive, [
    step.becomeActive$,
  ])

  return (
    <Step
      title={step.outlineDisplay.title}
      index={step.position.index}
      isLast={step.position.last}
      descriptionHtml={step.descriptionHtml}
      active={step.isActive}
      becomeActive$={step.becomeActive$}
      onBecomeVisible={step.becomeVisible}
      onBecomeHidden={step.becomeHidden}
      data-test={`step-${step.stepId}`}
    >
      {renderStepContent(stepContainer)}
    </Step>
  )
})

/**
 * Each step container represents a piece of the creation UI and its' associated view model.
 *
 * @param stepContainer
 */
function renderStepContent(stepContainer: StepContainer) {
  if (stepContainer instanceof NameInputViewModel) {
    return <NameInput vm={stepContainer} />
  }

  if (stepContainer instanceof WorkspaceSelectorViewModel) {
    return <CompactWorkspaceSelector vm={stepContainer} />
  }

  if (stepContainer instanceof OwnerSelectorViewModel) {
    return <CompactOwnerSelector vm={stepContainer} />
  }

  if (stepContainer instanceof TeamSelectorViewModel) {
    return <CompactTeamSelector vm={stepContainer} />
  }

  if (stepContainer instanceof RequestProviderSelectorViewModel) {
    return <CompactRequestProviderSelector vm={stepContainer} />
  }

  if (stepContainer instanceof LegendSelectorViewModel) {
    return <CompactLegendSelector vm={stepContainer} />
  }

  if (stepContainer instanceof QuestionStepViewModel) {
    const { EditorInputComponent } = getQuestionComponentRegistryEntry(
      stepContainer.question.type,
    )
    return (
      <EditorInputComponent
        vm={stepContainer.question}
        disabled={stepContainer.disabled}
        focus$={stepContainer.step.becomeActive$}
        onFocus={stepContainer.step.onBecameActive}
      />
    )
  }

  if (stepContainer instanceof PreviousProviderSelectionViewModel) {
    return <PreviousProviderSelector vm={stepContainer} />
  }

  if (stepContainer instanceof SubmissionViewModel) {
    return <FormSubmission vm={stepContainer} />
  }
}

/**
 * Component that renders a loader in the form.
 */
function LoadingFormContent() {
  return (
    <div className={styles.loading}>
      <Spinner />
    </div>
  )
}
